import { useMemo, useState, useCallback, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bars3Icon, XMarkIcon, EyeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import type { Entry, Project } from '../../services/api/timeEntry';
import { getHolidays } from '../../services/api/timeEntry';
import { CellEntriesPopover } from './CellEntriesPopover';
import { formatLocalDate } from '../../utils/format';
import { useTrackedTimeout } from '../../hooks/useTrackedTimeout';
import { isMacPlatform } from '../../utils/platform';
import {
  CELL_STATE_CLASS,
  ROW_STATE_BORDER,
  ROW_STATE_LABEL,
  TARGET_TONE_CLASS,
  dominantRowState,
  formatLoggedVsTarget,
  targetTitle,
  targetTone,
} from './totalsFormat';
import { ENGLISH_LABEL_FORMAT, formatMonthYear } from '../../utils/dateFormat';
import { LEGACY_CZ_NUMBER_LOCALE, compareText } from '../../utils/localeFormat';

interface LockedPeriod {
  from: string;
  to: string;
  state: string;
}

interface TimeOffDayInfo {
  hours: number;
  typeName: string;
}

export interface PastedEntryData {
  hours: number;
  description: string | null;
  task: string | null;
}

interface MonthGridProps {
  year: number;
  month: number; // 0-indexed
  entries: Entry[];
  projects: Project[];
  lockedPeriods?: LockedPeriod[];
  /**
   * Phase 113 (MKS-208, D-13) — `${projectId}-${YYYY}-${MM}` for every locked
   * project-month. A SECOND, independent lookup next to `lockedPeriods`, not an
   * extension of it: a `LockedPeriod` is date-only (submitting a timesheet
   * closes the whole month for one person across every project), while this one
   * is per (project, month) — project A can be closed for August while project
   * B stays open for the same person in the same month. Forcing the two into
   * one shape would make both harder to read.
   */
  projectMonthLocks?: Set<string>;
  previousPeriodProjectIds?: Set<number>;
  timeOffDays?: Map<string, TimeOffDayInfo>;
  onEditEntry?: (entry: Entry) => void;
  onDeleteEntry?: (entry: Entry) => void;
  onCreateEntry?: (date: string, projectId: number, pastedData?: PastedEntryData) => void;
  onMoveEntry?: (entryId: number, newDate: string, newProjectId: number) => void;
  readOnly?: boolean;
  selectedEntryIds?: Set<number>;
  onToggleEntrySelection?: (entryIds: number[]) => void;
  projectOrder?: number[];
  onProjectOrderChange?: (order: number[]) => void;
  showAllProjects?: boolean;
  onShowAllProjectsChange?: (show: boolean) => void;
  hiddenProjectIds?: Set<number>;
  onHideProject?: (projectId: number) => void;
  onUnhideProject?: (projectId: number) => void;
  /**
   * MKS-271 — the locked-month notice's button. Receives the ids of the
   * projects the viewed month is locked on, so the request dialog can offer
   * exactly those. Omitted (or `readOnly`) renders the notice without a button:
   * the state is still worth naming even where nothing can be filed.
   */
  onRequestBackdatedHours?: (lockedProjectIds: number[]) => void;
  /**
   * MKS-536 — the hours-neutral move exemption is on. A MONTH-locked cell then
   * still offers Edit and still takes part in a drag, as long as the drop would
   * leave the month's reported total alone; Add and Delete stay closed, and a
   * PERIOD-locked cell (a submitted or approved timesheet) is untouched — that
   * is a different lock with a different release path.
   *
   * False, the default, is today's behaviour exactly: every locked cell is
   * closed to everything.
   */
  lockedMonthMoveEnabled?: boolean;
}

export interface MonthGridHandle {
  focus: () => void;
}

function getTodayStr(): string {
  return formatLocalDate(new Date());
}

function isDateInLockedPeriod(date: string, lockedPeriods: LockedPeriod[]): LockedPeriod | null {
  for (const period of lockedPeriods) {
    if (date >= period.from && date <= period.to) {
      return period;
    }
  }
  return null;
}

/** Phase 113 (MKS-208) — is this cell's (project, month) closed to time entry? */
function isProjectMonthLocked(
  date: string,
  projectId: number,
  projectMonthLocks: Set<string>,
): boolean {
  return projectMonthLocks.has(`${projectId}-${date.slice(0, 7)}`);
}

/**
 * MKS-536 — would dropping `entry` on (`date`, `target`) leave every locked
 * month's reported total exactly as it was? The same rule the backend applies
 * (`isHoursNeutralMove`), minus the parts the grid cannot change by dragging:
 * a drag never edits hours, and the entry it carries is never on an issued
 * invoice (an invoiced entry is approved, so the cell is not draggable).
 *
 * The backend stays the authority — this only stops the grid OFFERING a drop
 * it already knows will be refused.
 */
function isNeutralDrop(
  entry: Entry,
  date: string,
  source: Project | undefined,
  target: Project,
): boolean {
  // Same calendar month.
  if (entry.date.slice(0, 7) !== date.slice(0, 7)) return false;
  // Same project — a move between tasks.
  if (entry.projectId === target.id) return true;
  if (!source) return false;
  // Same customer, or both Internal (`billable === false`).
  if (source.customerId != null && source.customerId === target.customerId) return true;
  return source.billable === false && target.billable === false;
}

/**
 * MKS-271 — the hatch that tells a locked project-month apart from an empty
 * open day. A diagonal grey stripe rather than a flat fill, because the grid
 * already spends flat greys on weekends and flat colours on entry states.
 */
const MONTH_LOCK_HATCH =
  'bg-[repeating-linear-gradient(45deg,#f3f4f6_0px,#f3f4f6_3px,#d1d5db_3px,#d1d5db_6px)]';

export const MonthGrid = forwardRef<MonthGridHandle, MonthGridProps>(function MonthGrid({
  year,
  month,
  entries,
  projects,
  lockedPeriods = [],
  projectMonthLocks = new Set(),
  previousPeriodProjectIds = new Set(),
  timeOffDays = new Map(),
  onEditEntry,
  onDeleteEntry,
  onCreateEntry,
  onMoveEntry,
  readOnly = false,
  selectedEntryIds = new Set(),
  onToggleEntrySelection,
  projectOrder: projectOrderProp = [],
  onProjectOrderChange,
  showAllProjects: showAllProjectsProp = false,
  onShowAllProjectsChange,
  hiddenProjectIds = new Set(),
  onHideProject,
  onUnhideProject,
  onRequestBackdatedHours,
  lockedMonthMoveEnabled = false,
}, ref) {
  const todayStr = getTodayStr();

  // Fetch Czech holidays for the year
  const { data: holidaysData } = useQuery({
    queryKey: ['holidays', year],
    queryFn: () => getHolidays(year),
    staleTime: 60 * 60 * 1000, // Cache for 1 hour (balance freshness vs server load)
  });

  // Create a Set of holiday dates for quick lookup
  const holidayDates = useMemo(() => {
    const dates = new Set<string>();
    if (holidaysData?.data) {
      for (const holiday of holidaysData.data) {
        dates.add(holiday.date);
      }
    }
    return dates;
  }, [holidaysData]);

  // Create a map of holiday names for tooltips
  const holidayNames = useMemo(() => {
    const names = new Map<string, string>();
    if (holidaysData?.data) {
      for (const holiday of holidaysData.data) {
        names.set(holiday.date, holiday.name);
      }
    }
    return names;
  }, [holidaysData]);

  // Generate all dates in the month
  const monthDates = useMemo(() => {
    const dates: string[] = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      dates.push(formatLocalDate(new Date(year, month, day)));
    }
    return dates;
  }, [year, month]);

  // Group entries by project and date (supports multiple entries per cell)
  const entryMap = useMemo(() => {
    const map = new Map<string, Entry[]>();
    for (const entry of entries) {
      const key = `${entry.projectId}-${entry.date}`;
      const existing = map.get(key) || [];
      existing.push(entry);
      map.set(key, existing);
    }
    return map;
  }, [entries]);

  // Get unique projects from entries and available projects
  const baseProjects = useMemo(() => {
    const projectIds = new Set<number>();
    const result: Project[] = [];

    for (const entry of entries) {
      if (!projectIds.has(entry.projectId)) {
        projectIds.add(entry.projectId);
        const project = projects.find((p) => p.id === entry.projectId);
        if (project) {
          result.push(project);
        } else {
          result.push({
            id: entry.projectId,
            name: entry.project.name,
            fullName: entry.project.fullName,
            state: 'in_progress',
            isDefault: false,
            mandatoryTasks: false,
            defaultRate: null,
            customer: null,
          });
        }
      }
    }

    for (const project of projects) {
      if (project.isDefault && !projectIds.has(project.id)) {
        projectIds.add(project.id);
        result.push(project);
      }
    }

    return result;
  }, [entries, projects]);

  // Project ordering and show-all state come from props (server-backed)
  const projectOrder = projectOrderProp;
  const showAllProjects = showAllProjectsProp;

  const handleToggleShowAll = useCallback((checked: boolean) => {
    onShowAllProjectsChange?.(checked);
  }, [onShowAllProjectsChange]);

  // Get IDs of projects with entries in current month
  const currentMonthProjectIds = useMemo(() => {
    const ids = new Set<number>();
    for (const entry of entries) {
      if (entry.hours > 0) {
        ids.add(entry.projectId);
      }
    }
    return ids;
  }, [entries]);

  // Filter projects based on toggle: show active (previous period + current) or all
  const filteredProjects = useMemo(() => {
    if (showAllProjects) {
      // When "All" is checked, show ALL available projects (assigned + default)
      const projectIds = new Set(baseProjects.map((p) => p.id));
      const allProjects = [...baseProjects];
      for (const project of projects) {
        if (!projectIds.has(project.id)) {
          allProjects.push(project);
        }
      }
      return allProjects;
    }

    // Default: show projects active in current or previous month
    const seen = new Set<number>();
    const result = baseProjects.filter((p) => {
      if (currentMonthProjectIds.has(p.id) || previousPeriodProjectIds.has(p.id)) {
        seen.add(p.id);
        return true;
      }
      return false;
    });
    // Also include assigned projects from previous period not already in baseProjects
    for (const project of projects) {
      if (!seen.has(project.id) && previousPeriodProjectIds.has(project.id)) {
        result.push(project);
      }
    }
    return result;
  }, [baseProjects, projects, showAllProjects, currentMonthProjectIds, previousPeriodProjectIds]);

  // Filter out projects with 0 hours that shouldn't be shown:
  // - readOnly: always hide 0-hour projects (no point showing empty rows on locked timesheets)
  // - user-hidden: hide unless project has hours (auto-unhide)
  // - Show All: keep hidden projects (dimmed) so user can unhide them
  const visibleProjects = useMemo(() => {
    return filteredProjects.filter((p) => {
      const hasHours = currentMonthProjectIds.has(p.id);
      // Always show projects with hours
      if (hasHours) return true;
      // In readOnly mode, never show 0-hour projects
      if (readOnly) return false;
      // If user hid this project and Show All is off, hide it
      if (hiddenProjectIds.has(p.id) && !showAllProjects) return false;
      // Otherwise show it (including dimmed hidden ones when Show All is on)
      return true;
    });
  }, [filteredProjects, hiddenProjectIds, currentMonthProjectIds, showAllProjects, readOnly]);

  // MKS-536 — the dragged entry's SOURCE project, which the neutrality rule
  // reads (customer, Internal). Built over every project handed to the grid,
  // not just the displayed rows: a row can be hidden and still be dragged from.
  const projectById = useMemo(
    () => new Map(projects.map((p) => [p.id, p])),
    [projects],
  );

  // Sort projects based on saved order, then alphabetically.
  // Hidden+0-hour projects are pushed to the end when Show All is on.
  const displayProjects = useMemo(() => {
    const isHiddenDimmed = (p: Project) =>
      showAllProjects && hiddenProjectIds.has(p.id) && !currentMonthProjectIds.has(p.id);

    if (projectOrder.length === 0) {
      return [...visibleProjects].sort((a, b) => {
        const aHidden = isHiddenDimmed(a) ? 1 : 0;
        const bHidden = isHiddenDimmed(b) ? 1 : 0;
        if (aHidden !== bHidden) return aHidden - bHidden;
        const nameA = a.fullName || a.name || '';
        const nameB = b.fullName || b.name || '';
        return compareText(nameA, nameB, LEGACY_CZ_NUMBER_LOCALE);
      });
    }

    const orderMap = new Map(projectOrder.map((id, index) => [id, index]));
    return [...visibleProjects].sort((a, b) => {
      // Hidden dimmed projects always go to the end
      const aHidden = isHiddenDimmed(a) ? 1 : 0;
      const bHidden = isHiddenDimmed(b) ? 1 : 0;
      if (aHidden !== bHidden) return aHidden - bHidden;

      const orderA = orderMap.get(a.id);
      const orderB = orderMap.get(b.id);

      if (orderA !== undefined && orderB !== undefined) {
        return orderA - orderB;
      }
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;

      const nameA = a.fullName || a.name || '';
      const nameB = b.fullName || b.name || '';
      return compareText(nameA, nameB, LEGACY_CZ_NUMBER_LOCALE);
    });
  }, [visibleProjects, projectOrder, showAllProjects, hiddenProjectIds, currentMonthProjectIds]);

  // MKS-271 — which of the rows on screen are closed for the whole viewed
  // month. The lock is per (project, month) and the grid shows exactly one
  // month, so a locked row is locked end to end: one padlock in the row header
  // says it, and the notice above the grid names the projects.
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const lockedDisplayProjects = useMemo(
    () => displayProjects.filter((p) => projectMonthLocks.has(`${p.id}-${monthKey}`)),
    [displayProjects, projectMonthLocks, monthKey],
  );

  const monthLabel = useMemo(
    // MKS-351 — "October 2026" from the shared util. This heading already read
    // English before the ticket (it called en-US), so it passes the English
    // label spec and reads the same in both release-flag branches.
    () => formatMonthYear(new Date(year, month, 1), ENGLISH_LABEL_FORMAT),
    [year, month],
  );

  // Drag state
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, projectId: number) => {
    setDraggedId(projectId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(projectId));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, projectId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (projectId !== draggedId) {
      setDragOverId(projectId);
    }
  }, [draggedId]);

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    if (draggedId === null || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    const currentOrder = displayProjects.map((p) => p.id);
    const draggedIndex = currentOrder.indexOf(draggedId);
    const targetIndex = currentOrder.indexOf(targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    // Remove dragged item and insert at target position
    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedId);

    onProjectOrderChange?.(newOrder);

    setDraggedId(null);
    setDragOverId(null);
  }, [draggedId, displayProjects, onProjectOrderChange]);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  // Popover state for multi-entry cells
  const [popoverState, setPopoverState] = useState<{
    isOpen: boolean;
    date: string;
    projectId: number;
    projectName: string;
    entries: Entry[];
    position: { top: number; left: number };
    isLocked: boolean;
    // MKS-536 — month-locked only, with the exemption on: Edit is still offered.
    lockedEditAllowed: boolean;
  } | null>(null);

  // Entry drag state
  const [draggedEntry, setDraggedEntry] = useState<Entry | null>(null);
  const [dropTarget, setDropTarget] = useState<{ date: string; projectId: number } | null>(null);

  // Active cell for keyboard navigation
  const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(null);
  const cellRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  // F1-009: track the row-swap focus-follow timeout so we can cancel it
  // on unmount or before scheduling a new one — prevents .focus() firing
  // on a stale ref after the grid re-renders with a different key set.
  const focusFollow = useTrackedTimeout();

  // Clipboard state for copy/paste
  const [copiedEntry, setCopiedEntry] = useState<PastedEntryData | null>(null);

  const tableRef = useRef<HTMLDivElement>(null);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => {
      tableRef.current?.focus();
    },
  }), []);

  // Calculate daily totals (including time off)
  const dailyTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const entry of entries) {
      totals[entry.date] = (totals[entry.date] || 0) + entry.hours;
    }
    // Include time off hours in daily totals
    for (const [date, info] of timeOffDays.entries()) {
      totals[date] = (totals[date] || 0) + info.hours;
    }
    return totals;
  }, [entries, timeOffDays]);

  // Calculate month total (including time off)
  const monthTotal = useMemo(() => {
    const entryTotal = entries.reduce((sum, e) => sum + e.hours, 0);
    const timeOffTotal = Array.from(timeOffDays.entries())
      .filter(([date]) => monthDates.includes(date))
      .reduce((sum, [, info]) => sum + info.hours, 0);
    return entryTotal + timeOffTotal;
  }, [entries, timeOffDays, monthDates]);

  // Calculate max working hours for the month (working days × 8 hours)
  const maxWorkingHours = useMemo(() => {
    let workingDays = 0;
    for (const date of monthDates) {
      const dayNum = parseInt(date.split('-')[2] || '1', 10);
      const dayOfWeek = new Date(year, month, dayNum).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHoliday = holidayDates.has(date);
      if (!isWeekend && !isHoliday) {
        workingDays++;
      }
    }
    return workingDays * 8;
  }, [monthDates, holidayDates, year, month]);

  // Calculate weekly totals (weeks start on Monday)
  const weeklyData = useMemo(() => {
    const weeks: { dates: string[]; total: number; expected: number }[] = [];
    let currentWeek: string[] = [];
    let currentWeekTotal = 0;
    let currentWeekExpected = 0;

    for (const date of monthDates) {
      const dayNum = parseInt(date.split('-')[2] || '1', 10);
      const dayOfWeek = new Date(year, month, dayNum).getDay();

      // Start new week on Monday (dayOfWeek === 1) if we have accumulated days
      if (dayOfWeek === 1 && currentWeek.length > 0) {
        weeks.push({ dates: currentWeek, total: currentWeekTotal, expected: currentWeekExpected });
        currentWeek = [];
        currentWeekTotal = 0;
        currentWeekExpected = 0;
      }

      currentWeek.push(date);
      currentWeekTotal += dailyTotals[date] || 0;
      // Expected (target) hours for the week: weekdays × 8, minus public holidays.
      // Partial weeks at month boundaries count only this month's portion (matches total).
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      if (!isWeekend && !holidayDates.has(date)) currentWeekExpected += 8;
    }

    // Push the last week
    if (currentWeek.length > 0) {
      weeks.push({ dates: currentWeek, total: currentWeekTotal, expected: currentWeekExpected });
    }

    return weeks;
  }, [monthDates, dailyTotals, year, month, holidayDates]);

  const handleCellClick = useCallback(
    (
      e: React.MouseEvent | { currentTarget: HTMLElement },
      date: string,
      projectId: number,
      project: Project,
      isLocked: boolean,
      // MKS-536 — defaults to false, so the keyboard call site below keeps
      // today's behaviour until it is told otherwise.
      lockedEditAllowed = false
    ) => {
      const cellEntries = entryMap.get(`${projectId}-${date}`) || [];

      // If no entries and not readonly, create new one directly
      if (cellEntries.length === 0) {
        if (!readOnly && onCreateEntry) {
          onCreateEntry(date, projectId);
        }
        return;
      }

      // In readOnly mode with selection handler, toggle selection on click
      if (readOnly && onToggleEntrySelection) {
        const entryIds = cellEntries.map((e) => e.id);
        onToggleEntrySelection(entryIds);
        return;
      }

      // Has entries - show popover (in readOnly mode without selection, just for viewing)
      const rect = e.currentTarget.getBoundingClientRect();
      setPopoverState({
        isOpen: true,
        date,
        projectId,
        projectName: project.fullName || project.name,
        entries: cellEntries,
        position: {
          top: rect.bottom + 4,
          left: Math.max(8, rect.left - 100), // Center-ish, but keep on screen
        },
        isLocked,
        lockedEditAllowed,
      });
    },
    [entryMap, readOnly, onCreateEntry, onToggleEntrySelection]
  );

  const handlePopoverClose = useCallback(() => {
    setPopoverState(null);
  }, []);

  const handlePopoverEdit = useCallback(
    (entry: Entry) => {
      if (readOnly || !onEditEntry) return;
      setPopoverState(null);
      onEditEntry(entry);
    },
    [onEditEntry, readOnly]
  );

  const handlePopoverDelete = useCallback(
    (entry: Entry) => {
      if (readOnly || !onDeleteEntry) return;
      setPopoverState(null);
      onDeleteEntry(entry);
    },
    [onDeleteEntry, readOnly]
  );

  const handlePopoverAdd = useCallback(() => {
    if (readOnly || !onCreateEntry || !popoverState) return;
    setPopoverState(null);
    onCreateEntry(popoverState.date, popoverState.projectId);
  }, [popoverState, onCreateEntry, readOnly]);

  // Entry drag handlers
  const handleEntryDragStart = useCallback(
    (e: React.DragEvent, entry: Entry) => {
      if (readOnly || !onMoveEntry) return;
      e.stopPropagation(); // Prevent row drag
      setDraggedEntry(entry);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', entry.id.toString());
    },
    [readOnly, onMoveEntry]
  );

  const handleCellDragOver = useCallback(
    (e: React.DragEvent, date: string, projectId: number) => {
      if (!draggedEntry || readOnly) return;
      e.preventDefault();
      e.stopPropagation();
      setDropTarget({ date, projectId });
    },
    [draggedEntry, readOnly]
  );

  const handleCellDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleCellDrop = useCallback(
    // MKS-536 — `canDrop` is the cell's own verdict, computed where the lock
    // state lives. It only ever says `false` while the exemption is on: with
    // the flag off every cell passes `true` and this is the drop it always was.
    (e: React.DragEvent, date: string, projectId: number, canDrop: boolean) => {
      e.preventDefault();
      e.stopPropagation();
      if (!draggedEntry || !onMoveEntry) return;

      // Only move if target is different
      if (canDrop && (draggedEntry.date !== date || draggedEntry.projectId !== projectId)) {
        onMoveEntry(draggedEntry.id, date, projectId);
      }

      setDraggedEntry(null);
      setDropTarget(null);
    },
    [draggedEntry, onMoveEntry]
  );

  const handleEntryDragEnd = useCallback(() => {
    setDraggedEntry(null);
    setDropTarget(null);
  }, []);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (displayProjects.length === 0 || monthDates.length === 0) return;

      const maxRow = displayProjects.length - 1;
      const maxCol = monthDates.length - 1;
      const modKey = isMacPlatform() ? e.metaKey : e.ctrlKey;

      // Handle copy/paste with active cell
      if (modKey && activeCell) {
        const project = displayProjects[activeCell.row];
        const date = monthDates[activeCell.col];

        if (!project || !date) return;

        if (e.key === 'c' || e.key === 'C') {
          // Copy
          e.preventDefault();
          e.stopPropagation();
          const cellEntries = entryMap.get(`${project.id}-${date}`) || [];
          const firstEntry = cellEntries[0];
          if (firstEntry) {
            setCopiedEntry({
              hours: firstEntry.hours,
              description: firstEntry.description,
              task: firstEntry.task,
            });
          }
          // Maintain focus on active cell
          const cellKey = `${activeCell.row}-${activeCell.col}`;
          cellRefs.current.get(cellKey)?.focus();
          return;
        }

        if (e.key === 'v' || e.key === 'V') {
          // Paste
          e.preventDefault();
          e.stopPropagation();
          if (copiedEntry && onCreateEntry && !readOnly) {
            const lockedPeriod = isDateInLockedPeriod(date, lockedPeriods);
            const isLocked =
              (!!lockedPeriod && lockedPeriod.state !== 'rejected') ||
              isProjectMonthLocked(date, project.id, projectMonthLocks);
            if (!isLocked) {
              onCreateEntry(date, project.id, copiedEntry);
            }
          }
          return;
        }
      }

      // Handle backspace/delete/d to remove entries from active cell
      if ((e.key === 'Backspace' || e.key === 'Delete' || e.key === 'd' || e.key === 'D') && activeCell && onDeleteEntry && !readOnly) {
        const project = displayProjects[activeCell.row];
        const date = monthDates[activeCell.col];

        if (!project || !date) return;

        const lockedPeriod = isDateInLockedPeriod(date, lockedPeriods);
        const isLocked =
          (!!lockedPeriod && lockedPeriod.state !== 'rejected') ||
          isProjectMonthLocked(date, project.id, projectMonthLocks);
        if (isLocked) return;

        const cellEntries = entryMap.get(`${project.id}-${date}`) || [];
        // Only delete editable entries (open or rejected state)
        const deletableEntries = cellEntries.filter(entry => entry.state === 'open' || entry.state === 'rejected');

        if (deletableEntries.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          // Delete all deletable entries in the cell
          for (const entry of deletableEntries) {
            onDeleteEntry(entry);
          }
        }
        return;
      }

      // If no active cell, set to first cell on any arrow key
      if (!activeCell) {
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          e.preventDefault();
          setActiveCell({ row: 0, col: 0 });
          cellRefs.current.get('0-0')?.focus();
        }
        return;
      }

      // Shift+Arrow Up/Down: move current row up/down
      if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const targetRow = e.key === 'ArrowUp' ? activeCell.row - 1 : activeCell.row + 1;
        if (targetRow < 0 || targetRow > maxRow) return;

        const currentOrder = displayProjects.map((p) => p.id);
        const newOrder = [...currentOrder];
        // Swap adjacent rows (bounds already checked above)
        const tmp = newOrder[activeCell.row]!;
        newOrder[activeCell.row] = newOrder[targetRow]!;
        newOrder[targetRow] = tmp;

        onProjectOrderChange?.(newOrder);
        setActiveCell({ row: targetRow, col: activeCell.col });
        // Focus will follow after re-render via useEffect
        focusFollow.schedule(() => {
          cellRefs.current.get(`${targetRow}-${activeCell.col}`)?.focus();
        }, 0);
        return;
      }

      let newRow = activeCell.row;
      let newCol = activeCell.col;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          newRow = Math.max(0, activeCell.row - 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          newRow = Math.min(maxRow, activeCell.row + 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          newCol = Math.max(0, activeCell.col - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          newCol = Math.min(maxCol, activeCell.col + 1);
          break;
        case 'Enter':
        case ' ': {
          e.preventDefault();
          const project = displayProjects[activeCell.row];
          const date = monthDates[activeCell.col];
          if (!project || !date) return;
          const lockedPeriod = isDateInLockedPeriod(date, lockedPeriods);
          const periodLocked = !!lockedPeriod && lockedPeriod.state !== 'rejected';
          const monthLocked = isProjectMonthLocked(date, project.id, projectMonthLocks);
          const isLocked = periodLocked || monthLocked;
          const cellEl = cellRefs.current.get(`${activeCell.row}-${activeCell.col}`);
          if (!cellEl) return;
          // MKS-536 — the keyboard opens the same popover as a double-click, so
          // it offers the same move in a month-locked cell.
          handleCellClick(
            { currentTarget: cellEl },
            date,
            project.id,
            project,
            isLocked,
            lockedMonthMoveEnabled && monthLocked && !periodLocked,
          );
          return;
        }
        case 'Escape':
          e.preventDefault();
          setActiveCell(null);
          return;
        default:
          return;
      }

      if (newRow !== activeCell.row || newCol !== activeCell.col) {
        setActiveCell({ row: newRow, col: newCol });
        cellRefs.current.get(`${newRow}-${newCol}`)?.focus();
      }
    },
    [activeCell, displayProjects, monthDates, entryMap, copiedEntry, onCreateEntry, onDeleteEntry, readOnly, lockedPeriods, projectMonthLocks, lockedMonthMoveEnabled, handleCellClick, onProjectOrderChange, focusFollow]
  );

  // Attach keyboard listener
  useEffect(() => {
    const container = tableRef.current;
    if (!container) return;

    container.addEventListener('keydown', handleKeyDown as EventListener);
    return () => {
      container.removeEventListener('keydown', handleKeyDown as EventListener);
    };
  }, [handleKeyDown]);

  return (
    <>
      {/* MKS-271 — the lock used to be discoverable only by hovering a cell or
          by failing to save. One line above the grid names the closed projects
          and offers the one way hours still get in. */}
      {lockedDisplayProjects.length > 0 && (
        <div
          role="status"
          className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          <LockClosedIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span>
            <span className="font-medium">{monthLabel}</span> is locked on{' '}
            <span className="font-medium">
              {lockedDisplayProjects.map((p) => p.fullName || p.name).join(', ')}
            </span>
            . Missing hours →
          </span>
          {!readOnly && onRequestBackdatedHours && (
            <button
              type="button"
              onClick={() => onRequestBackdatedHours(lockedDisplayProjects.map((p) => p.id))}
              className="font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
            >
              Request backdated hours
            </button>
          )}
        </div>
      )}

      <div className="overflow-auto max-h-[calc(100vh-12rem)] relative outline-none" ref={tableRef} tabIndex={0}>
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20">
            <tr className="bg-white">
              <th className="py-1 px-2 text-left font-medium text-gray-700 w-[280px] max-w-[280px] sticky left-0 bg-white z-30">
                <div className="flex items-center justify-between">
                  <span>Project</span>
                  <label className={`flex items-center gap-1 text-[10px] font-normal cursor-pointer ${
                    displayProjects.length === 0 && !showAllProjects
                      ? 'text-blue-600 font-medium'
                      : 'text-gray-500'
                  }`}>
                    <input
                      type="checkbox"
                      checked={showAllProjects}
                      onChange={(e) => handleToggleShowAll(e.target.checked)}
                      className={`h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                        displayProjects.length === 0 && !showAllProjects
                          ? 'animate-pulse ring-2 ring-blue-400 ring-offset-1'
                          : ''
                      }`}
                    />
                    All
                  </label>
                </div>
              </th>
              {monthDates.map((date) => {
                const dayNum = parseInt(date.split('-')[2] || '1', 10);
                const dayOfWeek = new Date(year, month, dayNum).getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const isHoliday = holidayDates.has(date);
                const holidayName = holidayNames.get(date);
                const isToday = date === todayStr;

                return (
                  <th
                    key={date}
                    className={`py-1 px-0 w-8 text-center font-normal ${
                      isHoliday ? 'bg-blue-50' : isWeekend ? 'bg-gray-50' : 'bg-white'
                    }`}
                    title={holidayName}
                  >
                    <div className={`text-[10px] ${isHoliday ? 'text-blue-400' : 'text-gray-400'}`}>
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][dayOfWeek]}
                    </div>
                    <div
                      className={`text-xs ${
                        isToday
                          ? 'bg-blue-600 text-white rounded-full w-5 h-5 mx-auto flex items-center justify-center'
                          : isHoliday
                            ? 'text-blue-500 font-medium'
                            : isWeekend
                              ? 'text-gray-400'
                              : 'text-gray-600'
                      }`}
                    >
                      {dayNum}
                    </div>
                    {/* MKS-315 — a holiday name never fitted a 32px column
                        ("Den v…"), so the column now carries a marker and the
                        `title` above puts the full name in the tooltip. */}
                    {isHoliday && holidayName && (
                      <div className="flex justify-center leading-none">
                        <span
                          data-holiday-marker="true"
                          className="mt-0.5 h-1.5 w-1.5 rounded-full bg-blue-500"
                          aria-hidden="true"
                        />
                        <span className="sr-only">{`Public holiday: ${holidayName}`}</span>
                      </div>
                    )}
                  </th>
                );
              })}
              <th className="py-1 px-2 text-right font-medium text-gray-700 sticky right-0 bg-white z-30 w-24">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Time Off pinned as first row */}
            {monthDates.some((date) => timeOffDays.has(date)) && (
              <tr className="border-b-2 border-indigo-200 bg-indigo-50">
                <td className="py-1 px-2 font-medium text-indigo-700 text-xs sticky left-0 bg-indigo-50 z-10">
                  Time Off
                </td>
                {monthDates.map((date) => {
                  const timeOff = timeOffDays.get(date);
                  return (
                    <td
                      key={date}
                      className="py-0.5 px-0"
                    >
                      {timeOff && (
                        <div
                          className="w-full h-6 flex items-center justify-center text-[10px] font-medium bg-indigo-200 text-indigo-800 rounded-sm"
                          title={`${timeOff.typeName}: ${timeOff.hours}h`}
                        >
                          {timeOff.hours}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="py-1 px-2 text-right font-medium text-indigo-700 text-xs sticky right-0 bg-indigo-50 z-10">
                  {Array.from(timeOffDays.entries())
                    .filter(([date]) => monthDates.includes(date))
                    .reduce((sum, [, info]) => sum + info.hours, 0)
                    .toFixed(1)}
                </td>
              </tr>
            )}
            {displayProjects.map((project, rowIndex) => {
              const rowEntries = monthDates.flatMap(
                (date) => entryMap.get(`${project.id}-${date}`) ?? [],
              );
              const projectTotal = rowEntries.reduce((sum, e) => sum + e.hours, 0);
              // MKS-315 — the row's state as a thin left border on the project
              // name, replacing the wash that used to paint every cell in the
              // row (including the empty ones) the state colour.
              const rowState = dominantRowState(rowEntries);
              const isDragging = draggedId === project.id;
              const isDragOver = dragOverId === project.id;
              // MKS-271 — the viewed month is closed on this project, end to end.
              const rowMonthLocked = projectMonthLocks.has(`${project.id}-${monthKey}`);
              const isEvenRow = rowIndex % 2 === 0;
              const rowBg = isEvenRow ? 'bg-white' : 'bg-gray-100';

              return (
                <tr
                  key={project.id}
                  draggable={!readOnly}
                  onDragStart={readOnly ? undefined : (e) => handleDragStart(e, project.id)}
                  onDragOver={readOnly ? undefined : (e) => handleDragOver(e, project.id)}
                  onDragLeave={readOnly ? undefined : handleDragLeave}
                  onDrop={readOnly ? undefined : (e) => handleDrop(e, project.id)}
                  onDragEnd={readOnly ? undefined : handleDragEnd}
                  className={`group/row border-b border-gray-100 hover:bg-blue-50/50 ${rowBg} ${
                    isDragging ? 'opacity-50' : ''
                  } ${isDragOver ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                >
                  <td
                    className={`py-1 px-2 w-[280px] max-w-[280px] sticky left-0 z-10 ${isEvenRow ? 'bg-white' : 'bg-gray-100'} ${
                      rowState ? `border-l-2 ${ROW_STATE_BORDER[rowState]}` : ''
                    }`}
                    title={project.fullName || project.name}
                  >
                    <div className="flex items-center gap-1">
                      {rowState && <span className="sr-only">{ROW_STATE_LABEL[rowState]}</span>}
                      {!readOnly && (
                        <Bars3Icon className="w-3 h-3 text-gray-400 cursor-grab flex-shrink-0" />
                      )}
                      {rowMonthLocked && (
                        <LockClosedIcon
                          role="img"
                          className="w-3 h-3 text-amber-600 flex-shrink-0"
                          aria-label={`${monthLabel} is locked on this project`}
                        />
                      )}
                      <span className={`font-medium truncate block ${
                        hiddenProjectIds.has(project.id) && !currentMonthProjectIds.has(project.id)
                          ? 'text-gray-400'
                          : 'text-gray-900'
                      }`}>
                        {project.fullName || project.name}
                      </span>
                      {!readOnly && hiddenProjectIds.has(project.id) && !currentMonthProjectIds.has(project.id) && onUnhideProject && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onUnhideProject(project.id); }}
                          className="ml-auto flex-shrink-0 p-0.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                          title="Show project"
                        >
                          <EyeIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!readOnly && projectTotal === 0 && !hiddenProjectIds.has(project.id) && onHideProject && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onHideProject(project.id); }}
                          className="ml-auto flex-shrink-0 p-0.5 text-gray-300 hover:text-red-500 rounded transition-colors opacity-0 group-hover/row:opacity-100"
                          title="Hide project for this month"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                  {monthDates.map((date, colIndex) => {
                    const cellEntries = entryMap.get(`${project.id}-${date}`) || [];
                    const cellTotal = cellEntries.reduce((s, e) => s + e.hours, 0);
                    const hasMultiple = cellEntries.length > 1;
                    const dayNum = parseInt(date.split('-')[2] || '1', 10);
                    const dayOfWeek = new Date(year, month, dayNum).getDay();
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    const isHoliday = holidayDates.has(date);
                    const lockedPeriod = isDateInLockedPeriod(date, lockedPeriods);
                    // Rejected periods are not locked - entries should be editable
                    // Phase 113 (MKS-208): a locked project-month closes the cell
                    // too, independently of the timesheet-submission lock.
                    const monthLocked = isProjectMonthLocked(date, project.id, projectMonthLocks);
                    const periodLocked = !!lockedPeriod && lockedPeriod.state !== 'rejected';
                    const isLocked = periodLocked || monthLocked;
                    // MKS-271 — an empty cell in a locked project-month is hatched, so it
                    // reads as closed without a hover. A submitted/approved timesheet already
                    // paints the cell yellow/green, and that colour stays: it is distinct
                    // from an open day on its own.
                    // MKS-315 — with the empty-cell wash gone, an empty cell in
                    // a locked project-month is plain whatever the timesheet
                    // state is, so the hatch is the only thing marking it.
                    const showMonthLockHatch = monthLocked && cellEntries.length === 0;
                    const allEditable = cellEntries.every((e) => e.state === 'open' || e.state === 'rejected');
                    // MKS-315 — the fill says the state of the hours that are
                    // actually in this cell. An empty cell is never painted:
                    // the pre-ticket grid washed every cell of a submitted
                    // month yellow, which is what made the numbers hard to read.
                    const cellState = dominantRowState(cellEntries);
                    const stateClass = cellState ? CELL_STATE_CLASS[cellState] : '';
                    // Check if any entry in this cell is selected
                    const hasSelected = cellEntries.some((e) => selectedEntryIds.has(e.id));

                    // MKS-536 — a MONTH-locked cell is only closed to adding and
                    // removing hours. With the exemption on it still gives up
                    // and takes an entry, as long as the drop is hours-neutral;
                    // a PERIOD-locked one never does.
                    const lockedMoveOnly = lockedMonthMoveEnabled && monthLocked && !periodLocked;
                    const dragBlocked = lockedMonthMoveEnabled ? periodLocked : isLocked;
                    // A locked month on EITHER side of the drop is what makes
                    // neutrality the condition; with neither side locked the
                    // drop is an ordinary one.
                    const lockedSideInvolved =
                      monthLocked ||
                      (!!draggedEntry &&
                        isProjectMonthLocked(
                          draggedEntry.date,
                          draggedEntry.projectId,
                          projectMonthLocks,
                        ));
                    const neutralDrop =
                      !!draggedEntry &&
                      isNeutralDrop(
                        draggedEntry,
                        date,
                        projectById.get(draggedEntry.projectId),
                        project,
                      );
                    const canDropHere = lockedMonthMoveEnabled
                      ? !periodLocked && (!lockedSideInvolved || neutralDrop)
                      : !isLocked;
                    // Only the ON branch ever refuses a drop outright. With the
                    // flag off the drop is made exactly as it always was and the
                    // backend answers for it — the ring is the only hint.
                    const dropAccepted = !lockedMonthMoveEnabled || canDropHere;

                    const isDropTarget = dropTarget?.date === date && dropTarget?.projectId === project.id;
                    const isDraggingThis = draggedEntry && cellEntries.some(e => e.id === draggedEntry.id);
                    const canDrag = !readOnly && !dragBlocked && cellEntries.length === 1 && allEditable && !!onMoveEntry;
                    const draggableEntry = canDrag ? cellEntries[0] : undefined;
                    const isActiveCell = activeCell?.row === rowIndex && activeCell?.col === colIndex;
                    const cellKey = `${rowIndex}-${colIndex}`;

                    return (
                      <td
                        key={date}
                        className={`py-0.5 px-0 overflow-visible relative ${isHoliday ? 'bg-blue-50/50' : isWeekend ? 'bg-gray-50/50' : ''}`}
                        onDragOver={draggedEntry ? (e) => handleCellDragOver(e, date, project.id) : undefined}
                        onDragLeave={draggedEntry ? handleCellDragLeave : undefined}
                        onDrop={draggedEntry ? (e) => handleCellDrop(e, date, project.id, dropAccepted) : undefined}
                      >
                        <button
                          ref={(el) => {
                            if (el) cellRefs.current.set(cellKey, el);
                            else cellRefs.current.delete(cellKey);
                          }}
                          tabIndex={isActiveCell ? 0 : -1}
                          draggable={canDrag}
                          onDragStart={draggableEntry ? (e) => handleEntryDragStart(e, draggableEntry) : undefined}
                          onDragEnd={canDrag ? handleEntryDragEnd : undefined}
                          onClick={() => {
                            setActiveCell({ row: rowIndex, col: colIndex });
                          }}
                          onDoubleClick={(e) => {
                            handleCellClick(e, date, project.id, project, isLocked, lockedMoveOnly);
                          }}
                          onFocus={() => setActiveCell({ row: rowIndex, col: colIndex })}
                          disabled={(isLocked || readOnly) && cellEntries.length === 0}
                          data-month-locked={showMonthLockHatch ? 'true' : undefined}
                          className={`
                            w-full h-6 text-center text-[10px] rounded-sm transition-colors relative overflow-visible
                            ${cellEntries.length > 0 ? 'font-medium' : ''}
                            ${stateClass}
                            ${showMonthLockHatch ? MONTH_LOCK_HATCH : ''}
                            ${cellEntries.length === 0 && !isLocked && !readOnly ? 'hover:bg-gray-100' : ''}
                            ${(isLocked || readOnly) && cellEntries.length === 0 ? 'cursor-default' : 'cursor-pointer'}
                            ${hasSelected ? 'ring-2 ring-blue-500 ring-inset z-20' : ''}
                            ${isDropTarget && canDropHere ? 'ring-2 ring-green-500 bg-green-50' : ''}
                            ${isDraggingThis ? 'opacity-50' : ''}
                            ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}
                            ${isActiveCell ? 'ring-2 ring-indigo-500 ring-offset-1 z-30' : ''}
                          `}
                          title={cellEntries.length > 0
                            ? cellEntries.map(e => `${e.hours}h${e.task ? ` - ${e.task}` : ''}${e.state === 'rejected' && e.rejectionReason ? ` [Rejected: ${e.rejectionReason}]` : ''}${e.description ? `: ${e.description}` : ''}`).join('\n')
                            : monthLocked
                              ? `${date} — this month is locked on this project`
                              : isLocked ? `${date} (${lockedPeriod?.state})` : date}
                        >
                          {cellTotal > 0 ? cellTotal : ''}
                          {hasMultiple && (
                            <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center leading-none font-medium z-20">
                              {cellEntries.length}
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                  <td className={`py-1 px-2 text-right font-medium text-gray-900 sticky right-0 z-10 ${isEvenRow ? 'bg-white' : 'bg-gray-100'}`}>
                    {projectTotal > 0 ? projectTotal.toFixed(1) : '-'}
                  </td>
                </tr>
              );
            })}

            {displayProjects.length === 0 && (
              <tr>
                <td colSpan={monthDates.length + 2} className="py-6 text-center text-gray-500 text-sm">
                  {readOnly
                    ? 'No time entries recorded.'
                    : !showAllProjects && baseProjects.length > 0
                      ? 'No active projects. Check "All" to see projects assigned to everyone.'
                      : 'No projects assigned. Contact your manager to get assigned to projects.'}
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200 bg-gray-50">
              <td className="py-1 px-2 font-semibold text-gray-900 text-xs sticky left-0 bg-gray-50 z-10">
                Daily
              </td>
              {monthDates.map((date) => {
                const dayNum = parseInt(date.split('-')[2] || '1', 10);
                const dayOfWeek = new Date(year, month, dayNum).getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const isHoliday = holidayDates.has(date);
                const total = dailyTotals[date];

                return (
                  <td
                    key={date}
                    className={`py-1 px-0 text-center text-[10px] font-medium ${
                      isHoliday ? 'bg-blue-100/50' : isWeekend ? 'bg-gray-100/50' : ''
                    } ${total ? 'text-gray-900' : 'text-gray-300'}`}
                  >
                    {total ? total.toFixed(1) : '-'}
                  </td>
                );
              })}
              <td className="py-1 px-2 sticky right-0 bg-gray-50 z-10">
              </td>
            </tr>
            <tr className="border-t border-gray-200 bg-gray-100">
              <td className="py-1 px-2 font-semibold text-gray-900 text-xs sticky left-0 bg-gray-100 z-10">
                Weekly
              </td>
              {/* MKS-315 — one cell per week spanning the week's columns, so
                  `18.5 h of 16 h` has room to say what the numbers are. The
                  pre-ticket row crammed `18.5 / 16` into the 32px Sunday
                  column, unitless and unlabelled. */}
              {weeklyData.map((week) => (
                <td
                  key={week.dates[0]}
                  colSpan={week.dates.length}
                  className={`py-1 px-1 text-center text-[10px] font-semibold whitespace-nowrap ${
                    TARGET_TONE_CLASS[targetTone(week.total, week.expected)]
                  }`}
                  title={targetTitle(week.total, week.expected)}
                >
                  {week.total > 0 || week.expected > 0
                    ? formatLoggedVsTarget(week.total, week.expected)
                    : ''}
                </td>
              ))}
              <td
                className={`py-1 px-2 text-right font-bold text-xs sticky right-0 bg-gray-100 z-10 whitespace-nowrap ${
                  TARGET_TONE_CLASS[targetTone(monthTotal, maxWorkingHours)]
                }`}
                title={targetTitle(monthTotal, maxWorkingHours)}
              >
                {formatLoggedVsTarget(monthTotal, maxWorkingHours)}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Popover for cells with multiple entries */}
        {popoverState?.isOpen && (
          <CellEntriesPopover
            entries={popoverState.entries}
            projectName={popoverState.projectName}
            date={popoverState.date}
            isLocked={popoverState.isLocked}
            lockedEditAllowed={popoverState.lockedEditAllowed}
            position={popoverState.position}
            onEdit={handlePopoverEdit}
            onDelete={handlePopoverDelete}
            onAdd={handlePopoverAdd}
            onClose={handlePopoverClose}
            readOnly={readOnly}
          />
        )}
      </div>
    </>
  );
});
